import { useEffect, useState } from 'react'
import { Button } from '@arcanecircle/ui/grimoire/button'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'
import { Input } from '@arcanecircle/ui/ui/input'
import { Switch } from '@arcanecircle/ui/ui/switch'
import { Slider } from '@arcanecircle/ui/ui/slider'
import type { MapConfig } from '@/types'

interface MapCreationModalProps {
  sessionId?: string
  onClose: () => void
  onCreate: (mapData: Partial<MapConfig>, imageFile: File | null) => Promise<void>
}

/**
 * v5 map creation modal — vtt-modal-* chrome (shared with the token
 * editor). Fields use Grimoire form primitives; danger-styled error
 * banner.
 */
export function MapCreationModal({ onClose, onCreate }: MapCreationModalProps) {
  const [name, setName] = useState('')
  const [width, setWidth] = useState(20)
  const [height, setHeight] = useState(20)
  const [gridSize, setGridSize] = useState(50)
  const [gridColor, setGridColor] = useState('#000000')
  const [gridOpacity, setGridOpacity] = useState(0.3)
  const [showGrid, setShowGrid] = useState(true)
  const [gridOffsetX, setGridOffsetX] = useState(0)
  const [gridOffsetY, setGridOffsetY] = useState(0)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB')
      return
    }
    setImageFile(file)
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Map name is required')
      return
    }
    if (width < 5 || width > 100) {
      setError('Width must be between 5 and 100')
      return
    }
    if (height < 5 || height > 100) {
      setError('Height must be between 5 and 100')
      return
    }
    setIsUploading(true)
    try {
      await onCreate(
        {
          name,
          width,
          height,
          gridSize,
          gridColor,
          gridOpacity,
          showGrid,
          gridOffsetX,
          gridOffsetY,
        },
        imageFile,
      )
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create map')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="vtt-modal-backdrop" onClick={onClose}>
      <form
        className="vtt-modal-surface"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{ maxWidth: 540 }}
      >
        <div className="vtt-modal-header">
          <div>
            <div className="ac-panel-eyebrow">Create</div>
            <div className="ac-panel-title">New map</div>
          </div>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Close"
            onClick={onClose}
          >
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </IconButton>
        </div>

        <div className="vtt-modal-body">
          {error && (
            <div
              style={{
                padding: '0.625rem 0.75rem',
                background: 'hsl(var(--ac-danger) / 0.15)',
                border: '1px solid hsl(var(--ac-danger) / 0.4)',
                color: 'hsl(var(--ac-danger))',
                borderRadius: 'var(--ac-radius)',
                fontSize: '0.75rem',
              }}
            >
              {error}
            </div>
          )}

          <div>
            <label className="ac-label">Map name</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Dragon's Lair"
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label className="ac-label">Width (squares)</label>
              <Input
                type="number"
                value={width}
                onChange={(e) => setWidth(parseInt(e.target.value) || 0)}
                min={5}
                max={100}
                required
              />
            </div>
            <div>
              <label className="ac-label">Height (squares)</label>
              <Input
                type="number"
                value={height}
                onChange={(e) => setHeight(parseInt(e.target.value) || 0)}
                min={5}
                max={100}
                required
              />
            </div>
          </div>

          <div>
            <label className="ac-label">Grid size (pixels)</label>
            <Input
              type="number"
              value={gridSize}
              onChange={(e) => setGridSize(parseInt(e.target.value) || 50)}
              min={20}
              max={100}
            />
          </div>

          <Switch checked={showGrid} onCheckedChange={setShowGrid}>
            Show grid
          </Switch>

          {showGrid && (
            <>
              <div>
                <label className="ac-label">Grid color</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={gridColor}
                    onChange={(e) => setGridColor(e.target.value)}
                    style={{
                      width: 44,
                      height: 40,
                      padding: 2,
                      borderRadius: 'var(--ac-radius)',
                      border: '1px solid hsl(var(--ac-dark-accent))',
                      background: 'hsl(var(--ac-dark-primary) / 0.6)',
                      cursor: 'pointer',
                    }}
                  />
                  <Input
                    type="text"
                    value={gridColor}
                    onChange={(e) => setGridColor(e.target.value)}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div>
                <label className="ac-label">
                  Grid opacity · {(gridOpacity * 100).toFixed(0)}%
                </label>
                <Slider
                  min={0}
                  max={1}
                  step={0.05}
                  value={[gridOpacity]}
                  onValueChange={([v]) => setGridOpacity(v)}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <label className="ac-label">Offset X</label>
                  <Input
                    type="number"
                    value={gridOffsetX}
                    onChange={(e) =>
                      setGridOffsetX(parseInt(e.target.value) || 0)
                    }
                  />
                </div>
                <div>
                  <label className="ac-label">Offset Y</label>
                  <Input
                    type="number"
                    value={gridOffsetY}
                    onChange={(e) =>
                      setGridOffsetY(parseInt(e.target.value) || 0)
                    }
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="ac-label">Background image (optional)</label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ padding: '0.375rem 0.75rem', height: 'auto' }}
            />
            {imageFile && (
              <div
                className="ac-help"
                style={{ marginTop: '0.375rem', color: 'hsl(var(--ac-success))' }}
              >
                Selected: {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>
        </div>

        <div className="vtt-modal-footer">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={isUploading}
          >
            {isUploading ? 'Creating…' : 'Create map'}
          </Button>
        </div>
      </form>
    </div>
  )
}
